import React, { useState, useEffect } from "react";
import { BrowserRouter, Routes, Route, Link, Navigate, useLocation } from "react-router-dom";
import api from "./lib/api";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { useLenis } from "./hooks/useLenis";
import { Reveal } from "./components/Reveal";
import Hero from "./sections/Hero";
import Process from "./sections/Process";
import WhyCalium from "./sections/WhyCalium";
import Testimonials from "./sections/Testimonials";
import Partners from "./sections/Partners";
import FAQ from "./sections/FAQ";
import ContactForm from "./sections/ContactForm";
import BlogPreview from "./sections/BlogPreview";
import { BlogListPage, BlogPostPage } from "./pages/BlogPages";
import AdminLogin from "./pages/AdminLogin";
import WhatsAppButton from "./components/WhatsAppButton";
import AdminTestimonials from "./components/admin/AdminTestimonials";
import AdminBlog from "./components/admin/AdminBlog";
import EmailStatusBanner from "./components/admin/EmailStatusBanner";
import AdminTeam from "./components/admin/AdminTeam";
import { ForgotPasswordPage, SetPasswordPage } from "./pages/PasswordPages";
import ServicesPage from "./pages/ServicesPage";
import SecurityPage from "./pages/SecurityPage";
import { SERVICES, COMPANY_PROFILE } from "./data/services";
import { 
  TrendingUp, 
  Layers, 
  Shield, 
  ArrowRight, 
  ArrowLeft, 
  Check, 
  X, 
  Menu, 
  Users, 
  BarChart, 
  Trash2, 
  Globe, 
  Sparkles, 
  ChevronRight,
  Mail,
  Phone,
  Briefcase,
  LogOut,
  MessageSquareQuote,
  Newspaper,
  UsersRound,
  Camera,
  LayoutTemplate,
  PenLine
} from "lucide-react";
import { HOME } from "@/constants/testIds";


// Toast Notification helper
const CustomToast = ({ message, type, onClose }) => {
  useEffect(() => {
    const timer = setTimeout(onClose, 5000);
    return () => clearTimeout(timer);
  }, [onClose]);

  return (
    <div className={`fixed bottom-5 right-5 z-50 p-4 border font-body rounded-md flex items-center justify-between gap-4 max-w-sm shadow-md transition-all duration-300 ${
      type === "success" 
        ? "bg-[#2E3A9E] text-white border-blue-400" 
        : "bg-[#6B1414] text-white border-red-300"
    }`}>
      <span className="text-sm font-semibold">{message}</span>
      <button onClick={onClose} className="hover:opacity-80">
        <X size={16} />
      </button>
    </div>
  );
};

// Main Navbar
const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();
  const { user } = useAuth();
  const isAdmin = location.pathname.startsWith("/admin") && !!user;

  return (
    <nav className="fixed top-0 left-0 w-full z-40 bg-white/75 backdrop-blur-xl border-b border-[#E5E5E5] transition-all">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" data-testid="nav-logo-link" className="flex items-center gap-3 group">
            <img src="/logo-mark.png" alt="Calium Seller Services logo" className="h-11 w-11 object-contain group-hover:rotate-6 transition-transform duration-300" />
            <span className="flex flex-col leading-none">
              <span className="font-heading text-xl font-extrabold tracking-tighter text-[#2E3A9E]">Calium</span>
              <span className="font-mono text-[9px] tracking-[0.2em] uppercase text-[#6B1414] font-bold">Seller Services</span>
            </span>
          </Link>

          {/* Desktop Menu */}
          <div className="hidden md:flex items-center gap-8">
            {!isAdmin ? (
              <>
                <Link to="/services" data-testid="nav-services" className="text-sm font-body font-medium text-[#0A0A0A] hover:text-[#2E3A9E] transition-colors">
                  Services
                </Link>
                <a href="#marketplaces" data-testid={HOME.navFeatures} className="text-sm font-body font-medium text-[#0A0A0A] hover:text-[#2E3A9E] transition-colors">
                  Marketplaces
                </a>
                <a href="#process" data-testid="nav-process" className="text-sm font-body font-medium text-[#0A0A0A] hover:text-[#2E3A9E] transition-colors">
                  Process
                </a>
                <a href="#estimator" data-testid={HOME.navEstimator} className="text-sm font-body font-medium text-[#0A0A0A] hover:text-[#2E3A9E] transition-colors">
                  Growth Estimator
                </a>
                <a href="#faq" data-testid="nav-faq" className="text-sm font-body font-medium text-[#0A0A0A] hover:text-[#2E3A9E] transition-colors">
                  FAQ
                </a>
                <Link to="/blog" data-testid="nav-blog" className="text-sm font-body font-medium text-[#0A0A0A] hover:text-[#2E3A9E] transition-colors">
                  Insights
                </Link>
                <a href="#contact" data-testid="nav-contact" className="text-sm font-body font-bold text-white bg-[#6B1414] hover:bg-[#521010] transition-colors px-4 py-2 rounded-full">
                  Free Audit
                </a>
              </>
            ) : (
              <>
                <Link to="/" className="text-sm font-body font-medium text-[#0A0A0A] hover:text-[#2E3A9E] transition-colors">
                  ← Back to Main Website
                </Link>
                <span className="font-mono text-xs tracking-widest text-[#6B1414] bg-red-50 px-2 py-1 border border-red-200 uppercase font-bold">
                  ADMIN ACTIVE
                </span>
              </>
            )}
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden flex items-center gap-4">
            {isAdmin && (
              <span className="font-mono text-[10px] tracking-widest text-[#6B1414] bg-red-50 px-1 py-0.5 border border-red-200 uppercase font-bold">
                ADMIN
              </span>
            )}
            <button
              onClick={() => setIsOpen(!isOpen)}
              data-testid="nav-mobile-toggle"
              aria-label="Toggle navigation menu"
              className="p-2 rounded-md text-[#0A0A0A] hover:text-[#2E3A9E]"
            >
              <Menu size={24} />
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {isOpen && (
        <div data-testid="nav-mobile-menu" className="md:hidden bg-white border-b border-[#E5E5E5] px-6 py-4 space-y-3">
          {!isAdmin ? (
            <>
              <Link 
                to="/services" 
                onClick={() => setIsOpen(false)}
                className="block text-sm font-body font-medium text-[#0A0A0A] py-2 border-b border-gray-100"
              >
                Services
              </Link>
              <a 
                href="#marketplaces" 
                onClick={() => setIsOpen(false)}
                className="block text-sm font-body font-medium text-[#0A0A0A] py-2 border-b border-gray-100"
              >
                Marketplaces
              </a>
              <a 
                href="#process" 
                onClick={() => setIsOpen(false)}
                className="block text-sm font-body font-medium text-[#0A0A0A] py-2 border-b border-gray-100"
              >
                Process
              </a>
              <a 
                href="#estimator" 
                onClick={() => setIsOpen(false)}
                className="block text-sm font-body font-medium text-[#0A0A0A] py-2 border-b border-gray-100"
              >
                Growth Estimator
              </a>
              <a 
                href="#faq" 
                onClick={() => setIsOpen(false)}
                className="block text-sm font-body font-medium text-[#0A0A0A] py-2 border-b border-gray-100"
              >
                FAQ
              </a>
              <Link 
                to="/blog" 
                onClick={() => setIsOpen(false)}
                className="block text-sm font-body font-medium text-[#0A0A0A] py-2 border-b border-gray-100"
              >
                Insights
              </Link>
              <a 
                href="#contact" 
                onClick={() => setIsOpen(false)}
                className="block text-sm font-body font-bold text-[#6B1414] py-2 border-b border-gray-100"
              >
                Free Audit
              </a>
            </>
          ) : (
            <>
              <Link 
                to="/" 
                onClick={() => setIsOpen(false)}
                className="block text-sm font-body font-medium text-[#0A0A0A] py-2"
              >
                ← Back to Main Website
              </Link>
            </>
          )}
        </div>
      )}
    </nav>
  );
};

// Core Services (from calium.in offerings)
const ServicesSection = () => {
  const icons = [Briefcase, TrendingUp, Sparkles, Layers, Globe, Shield, Camera, LayoutTemplate, PenLine];
  const services = SERVICES.map((s, i) => ({ icon: icons[i], title: s.title, desc: s.short, slug: s.slug }));

  return (
    <section id="services" className="py-24 bg-white border-b border-[#E5E5E5]">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="mb-16 grid grid-cols-1 lg:grid-cols-12 gap-8 items-end">
          <div className="lg:col-span-7 space-y-4">
            <span className="font-mono text-xs tracking-wider uppercase text-[#6B1414] font-bold">
              01 / What We Do
            </span>
            <h2 className="font-heading text-4xl sm:text-5xl font-extrabold tracking-tighter text-[#0A0A0A]">
              Seller Services, End to End
            </h2>
          </div>
          <div className="lg:col-span-5 space-y-4">
            <p className="font-body text-base text-[#6B7280] leading-relaxed">
              Amazon account management, advertising, training, cataloging, tax compliance, imaging, A+ Content and listing content — nine services, one accountable team, for marketplace sellers across India since 2017.
            </p>
            <Link to="/services" data-testid="services-view-all" className="inline-flex items-center gap-2 font-body font-bold text-sm text-[#0A0A0A] border-b-2 border-[#6B1414] pb-1 hover:text-[#6B1414]">Read detailed service descriptions <ArrowRight size={14} /></Link>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="services-grid">
          {services.map((s, i) => (
            <Link to={`/services/${s.slug}`} key={s.title} data-testid={`service-card-${i}`} className="group relative bg-[#FAFAFA] border border-[#E5E5E5] p-8 rounded-md hover:bg-[#2E3A9E] transition-colors duration-300 block">
              <div className="w-12 h-12 bg-white border border-[#E5E5E5] rounded-md flex items-center justify-center text-[#6B1414] mb-6 group-hover:border-transparent">
                <s.icon size={22} strokeWidth={1.75} aria-hidden="true" />
              </div>
              <span className="absolute top-6 right-6 font-mono text-[10px] text-gray-300 group-hover:text-white/50">0{i + 1}</span>
              <h3 className="font-heading text-lg font-bold text-[#0A0A0A] group-hover:text-white mb-2">{s.title}</h3>
              <p className="font-body text-sm text-[#6B7280] group-hover:text-blue-100 leading-relaxed">{s.desc}</p>
              <span className="mt-5 inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-wider text-[#6B1414] group-hover:text-[#F5D5B0]">Full description <ArrowRight size={12} /></span>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
};

// Marketplaces Grid with tooltip descriptions
const MarketplacesGrid = () => {
  const marketplaces = [
    {
      name: "Amazon India",
      role: "Global Reach & Primed Buyers",
      commission: "Competitive referral fee (2%-15%)",
      speed: "7-10 Days Launch",
      keyMetric: "₹10,00,000+ Potential Sales Lift",
      color: "border-l-4 border-l-[#6B1414]",
      details: "Full SEO copy, A+ catalog layout, Sponsored Ads architecture, FBA optimization, Buybox strategy."
    },
    {
      name: "Flipkart",
      role: "Massive Domestic Indian Footprint",
      commission: "Referral & closing fees starting at 3%",
      speed: "5-7 Days Launch",
      keyMetric: "2.5x Standard Buyer Discovery",
      color: "border-l-4 border-l-[#2E3A9E]",
      details: "Flipkart Ads setup, Assured Badge compliance, detailed catalog upload, bulk listing optimization."
    },
    {
      name: "Meesho",
      role: "Zero Commission Social Commerce",
      commission: "0% Commission platform fees",
      speed: "3-4 Days Launch",
      keyMetric: "3.2x Higher Volume Scale",
      color: "border-l-4 border-l-purple-600",
      details: "Low ASP marketing, Catalog uploading, automated ad campaigns, pricing calculations for deep tier-2/3 reach."
    },
    {
      name: "Myntra",
      role: "Premium Fashion & Lifestyle Hub",
      commission: "Premium fashion margin sharing",
      speed: "12-14 Days Launch",
      keyMetric: "Brand Uplift & Elite Status",
      color: "border-l-4 border-l-pink-500",
      details: "PPMP onboarding, premium model catalog shoots, event participation optimization, curated collection launches."
    },
    {
      name: "Ajio",
      role: "Curated Trend-Driven Audience",
      commission: "Commission-based premium structures",
      speed: "10-12 Days Launch",
      keyMetric: "High Repeat Purchase Ratio",
      color: "border-l-4 border-l-[#0A0A0A]",
      details: "AJIO Business Portal onboarding, JIT model inventory sync, fashion trend analysis and stock selection guidance."
    },
    {
      name: "JioMart",
      role: "Hyperlocal & Quick-Commerce Integration",
      commission: "Low grocery/category commissions",
      speed: "5-6 Days Launch",
      keyMetric: "Local High-Frequency Demand",
      color: "border-l-4 border-l-blue-400",
      details: "B2B onboarding, local supply chain catalog routing, hyper-local marketing, and inventory cataloging."
    }
  ];

  return (
    <section id="marketplaces" className="py-24 bg-white border-b border-[#E5E5E5]">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="mb-16 space-y-4">
          <span className="font-mono text-xs tracking-wider uppercase text-[#6B1414] font-bold">
            04 / Marketplace Coverage
          </span>
          <h2 className="font-heading text-4xl sm:text-5xl font-extrabold tracking-tighter text-[#0A0A0A]">
            Supported Platforms & Super-powers
          </h2>
          <p className="font-body text-base text-[#6B7280] max-w-3xl leading-relaxed">
            We don{"'"}t just register your company; we install custom sales systems for each. Click or hover on a platform to understand our specialized onboarding execution.
          </p>
        </div>

        {/* Exposed Skeleton Grid (Technical look) */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 border border-[#E5E5E5] bg-gray-50">
          {marketplaces.map((m, i) => (
            <div 
              key={i} 
              className={`bg-white p-8 border border-[#E5E5E5] flex flex-col justify-between hover:bg-gray-50/50 transition-all duration-300 relative group`}
            >
              {/* Overlay highlight */}
              <div className="absolute top-0 left-0 w-full h-1 bg-transparent group-hover:bg-[#2E3A9E] transition-all duration-300"></div>
              
              <div className="space-y-4">
                <div className={`p-1 pl-4 mb-4 ${m.color} bg-gray-50 flex justify-between items-center`}>
                  <h3 className="font-heading text-lg font-bold text-[#0A0A0A]">
                    {m.name}
                  </h3>
                  <span className="font-mono text-[10px] bg-white border border-gray-200 px-2 py-0.5 rounded text-gray-500 uppercase font-bold">
                    {m.speed}
                  </span>
                </div>
                <p className="font-body text-sm font-semibold text-[#2E3A9E]">
                  {m.role}
                </p>
                <p className="font-body text-sm text-[#6B7280] leading-relaxed">
                  {m.details}
                </p>
              </div>

              <div className="mt-8 pt-4 border-t border-[#E5E5E5] space-y-2 font-mono text-xs">
                <div className="flex justify-between">
                  <span className="text-gray-400">Fees:</span>
                  <span className="text-[#0A0A0A] font-medium">{m.commission}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Metric Boost:</span>
                  <span className="text-[#6B1414] font-extrabold uppercase">{m.keyMetric}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Interactive Growth Estimator
const GrowthEstimator = () => {
  const [sales, setSales] = useState(150000);
  const [marketplace, setMarketplace] = useState("Amazon");
  const [category, setCategory] = useState("Fashion");

  // Custom growth multiplier based on selected marketplace & category
  const multipliers = {
    Amazon: { Fashion: 2.2, Electronics: 1.8, Kitchen: 2.5, Cosmetics: 2.4 },
    Flipkart: { Fashion: 2.0, Electronics: 1.9, Kitchen: 2.2, Cosmetics: 2.1 },
    Meesho: { Fashion: 3.2, Electronics: 2.4, Kitchen: 2.8, Cosmetics: 3.5 },
    Myntra: { Fashion: 2.8, Electronics: 1.0, Kitchen: 1.5, Cosmetics: 2.6 },
    Ajio: { Fashion: 2.6, Electronics: 1.0, Kitchen: 1.2, Cosmetics: 2.4 },
    JioMart: { Fashion: 1.5, Electronics: 1.5, Kitchen: 2.0, Cosmetics: 1.8 },
  };

  const getMultiplier = () => {
    return multipliers[marketplace]?.[category] || 2.0;
  };

  const calculatedGrowth = sales * getMultiplier();
  const incrementalRevenue = (calculatedGrowth - sales) * 12;

  return (
    <section id="estimator" className="py-24 bg-[#FAFAFA] border-b border-[#E5E5E5]">
      <div className="max-w-7xl mx-auto px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
        {/* Visual context left-side */}
        <div className="lg:col-span-5 relative space-y-6">
          <span className="font-mono text-xs tracking-wider uppercase text-[#6B1414] font-bold block">
            06 / Predictive Engine
          </span>
          <h2 className="font-heading text-4xl sm:text-5xl font-extrabold tracking-tighter text-[#0A0A0A]">
            Calculate Your Untapped Marketplace Growth
          </h2>
          <p className="font-body text-base text-[#6B7280] leading-relaxed">
            Marketplaces function under different distribution dynamics. Enter your metrics, select your platform, and see how our consulting optimizations can compound your bottom line within 12 months.
          </p>

          <div className="border border-dashed border-[#2E3A9E]/40 p-6 bg-blue-50/20 relative rounded-md">
            <div className="absolute -top-3 left-6 font-mono text-[10px] bg-[#FAFAFA] border border-[#2E3A9E]/30 px-2 py-0.5 text-[#2E3A9E] uppercase font-bold">
              Algorithm Fact
            </div>
            <p className="font-body text-sm text-[#2E3A9E] leading-relaxed">
              * Meesho{"'"}s organic feed yields 3x unit multipliers for lower ASP catalog items. Amazon prime-badge integration increases checkout conversion by up to 34% inside Indian metro centers.
            </p>
          </div>
        </div>

        {/* Calculation Card widget right-side */}
        <div className="lg:col-span-7 bg-white border border-[#E5E5E5] p-8 md:p-10 rounded-md relative select-none">
          {/* Active border effect */}
          <div className="absolute top-0 left-0 w-full h-2 bg-[#2E3A9E] rounded-t-md"></div>
          
          <div className="space-y-8">
            {/* Inputs grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <div>
                <label className="block font-mono text-xs uppercase tracking-wider text-[#0A0A0A] mb-2 font-bold">
                  Target Marketplace
                </label>
                <select
                  data-testid={HOME.estimatorMarketplaceSelect}
                  value={marketplace}
                  onChange={(e) => setMarketplace(e.target.value)}
                  className="w-full bg-white border border-[#E5E5E5] p-3 font-body text-sm font-semibold text-[#0A0A0A] focus:outline-none focus:ring-2 focus:ring-[#2E3A9E] rounded-md"
                >
                  <option value="Amazon">Amazon India</option>
                  <option value="Flipkart">Flipkart</option>
                  <option value="Meesho">Meesho</option>
                  <option value="Myntra">Myntra</option>
                  <option value="Ajio">Ajio</option>
                  <option value="JioMart">JioMart</option>
                </select>
              </div>

              <div>
                <label className="block font-mono text-xs uppercase tracking-wider text-[#0A0A0A] mb-2 font-bold">
                  Product Category
                </label>
                <select
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  className="w-full bg-white border border-[#E5E5E5] p-3 font-body text-sm font-semibold text-[#0A0A0A] focus:outline-none focus:ring-2 focus:ring-[#2E3A9E] rounded-md"
                >
                  <option value="Fashion">Fashion & Apparel</option>
                  <option value="Electronics">Electronics & Accessories</option>
                  <option value="Kitchen">Kitchen & Home Essentials</option>
                  <option value="Cosmetics">Cosmetics & Wellness</option>
                </select>
              </div>
            </div>

            {/* Monthly Sales Slider */}
            <div>
              <div className="flex justify-between items-center mb-2">
                <span className="font-mono text-xs uppercase tracking-wider text-[#0A0A0A] font-bold">
                  Current Monthly Sales
                </span>
                <span className="font-mono text-sm font-extrabold text-[#2E3A9E] bg-blue-50 px-2 py-0.5 border border-blue-100 rounded">
                  ₹{sales.toLocaleString('en-IN')}
                </span>
              </div>
              <input
                type="range"
                data-testid={HOME.estimatorSalesSlider}
                min="10000"
                max="1000000"
                step="10000"
                value={sales}
                onChange={(e) => setSales(Number(e.target.value))}
                className="w-full accent-[#2E3A9E] h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
              />
              <div className="flex justify-between font-mono text-[10px] text-gray-400 mt-1">
                <span>₹10K</span>
                <span>₹5 Lakhs</span>
                <span>₹10 Lakhs</span>
              </div>
            </div>

            {/* Results Display Area */}
            <div className="bg-[#FAFAFA] border border-[#E5E5E5] p-6 grid grid-cols-1 sm:grid-cols-2 gap-6 items-center">
              <div>
                <p className="font-mono text-[10px] text-gray-500 uppercase tracking-widest mb-1">
                  Projected Monthly Sales
                </p>
                <p 
                  data-testid={HOME.estimatorProjectionDisplay}
                  className="font-heading text-2xl sm:text-3xl font-black text-[#2E3A9E] tracking-tight"
                >
                  ₹{Math.round(calculatedGrowth).toLocaleString('en-IN')}
                </p>
                <span className="inline-block mt-2 font-mono text-[10px] text-white bg-[#6B1414] px-2 py-0.5 font-bold uppercase rounded">
                  {getMultiplier()}x Growth Factor
                </span>
              </div>

              <div className="sm:border-l sm:border-[#E5E5E5] sm:pl-6">
                <p className="font-mono text-[10px] text-gray-500 uppercase tracking-widest mb-1">
                  12-Month Incremental Gain
                </p>
                <p className="font-heading text-2xl sm:text-3xl font-black text-[#6B1414] tracking-tight">
                  +₹{Math.round(incrementalRevenue).toLocaleString('en-IN')}
                </p>
                <p className="font-body text-xs text-gray-400 mt-2">
                  Estimated profit enhancement based on listing & ad tuning.
                </p>
              </div>
            </div>

            <div className="text-center pt-2">
              <a 
                href="#assessment"
                className="inline-flex items-center gap-2 text-sm font-body font-bold text-[#2E3A9E] hover:text-[#232D7C] group"
              >
                Apply for exact onboarding proposal <ChevronRight size={16} className="group-hover:translate-x-1 transition-transform" />
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Interactive Assessment Wizard + Lead Capture
const OnboardingWizard = ({ onLeadSuccess }) => {
  const [step, setStep] = useState(1);
  const [answers, setAnswers] = useState({
    businessStage: "",
    mainFriction: "",
    speedChoice: ""
  });

  const [leadForm, setLeadForm] = useState({
    clientName: "",
    businessName: "",
    email: "",
    phone: "",
    message: ""
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [formError, setFormError] = useState("");

  // Growth projection based on stage & goals
  const calculateWizardProjection = () => {
    let base = 250000;
    if (answers.businessStage === "Brand Owner") base = 500000;
    if (answers.businessStage === "Manufacturer") base = 800000;
    
    let mult = 1.8;
    if (answers.speedChoice === "Aggressive scaling") mult = 2.8;
    
    return base * mult;
  };

  const handleOptionSelect = (key, value) => {
    setAnswers({ ...answers, [key]: value });
  };

  const handleNext = () => {
    if (step === 1 && !answers.businessStage) return;
    if (step === 2 && !answers.mainFriction) return;
    if (step === 3 && !answers.speedChoice) return;
    setStep(step + 1);
  };

  const handlePrev = () => {
    setStep(step - 1);
  };

  const handleInputChange = (e) => {
    setLeadForm({ ...leadForm, [e.target.name]: e.target.value });
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    if (!leadForm.clientName || !leadForm.businessName || !leadForm.email || !leadForm.phone) {
      setFormError("Please fill in all mandatory fields.");
      return;
    }
    setFormError("");

    setIsSubmitting(true);
    const payload = {
      client_name: leadForm.clientName,
      business_name: leadForm.businessName,
      email: leadForm.email,
      phone: leadForm.phone,
      message: leadForm.message,
      current_revenue: answers.businessStage === "New Entrepreneur" ? 10000 : 150000,
      primary_marketplace: answers.mainFriction ? answers.mainFriction.split("on ")[1] || "All" : "Amazon",
      growth_projection: calculateWizardProjection(),
      assessment: {
        "Business Stage": answers.businessStage,
        "Friction Point": answers.mainFriction,
        "Desired Velocity": answers.speedChoice
      }
    };

    try {
      const response = await api.post(`/leads`, payload);
      if (response.data) {
        setSuccess(true);
        if (onLeadSuccess) onLeadSuccess();
        // Clear forms
        setAnswers({ businessStage: "", mainFriction: "", speedChoice: "" });
        setLeadForm({ clientName: "", businessName: "", email: "", phone: "", message: "" });
        setStep(5); // Success step
      }
    } catch (err) {
      console.error("Failed to submit lead", err);
      const detail = err?.response?.data?.detail;
      setFormError(Array.isArray(detail) ? detail.map((d) => d.msg).join(" ") : (typeof detail === "string" ? detail : "Something went wrong during submission. Please try again."));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="assessment" className="py-24 bg-white border-b border-[#E5E5E5]">
      <div className="max-w-4xl mx-auto px-6 lg:px-8">
        <div className="text-center mb-16 space-y-4">
          <span className="font-mono text-xs tracking-wider uppercase text-[#6B1414] font-bold block">
            07 / Growth Roadmap Onboarding
          </span>
          <h2 className="font-heading text-4xl sm:text-5xl font-extrabold tracking-tighter text-[#0A0A0A]">
            Build Your Marketplace Launch Plan
          </h2>
          <p className="font-body text-base text-[#6B7280] max-w-2xl mx-auto">
            Answer 3 quick structural questions to generate an onboarding audit and claim your 1-on-1 marketplace growth consultation.
          </p>
        </div>

        {/* Wizard Multi-step Container */}
        <div className="bg-white border border-[#E5E5E5] p-8 md:p-12 relative rounded-md select-none shadow-sm">
          {/* Progress bar */}
          <div className="absolute top-0 left-0 w-full h-1 bg-gray-100 rounded-t-md">
            <div 
              className="bg-[#2E3A9E] h-full transition-all duration-300"
              style={{ width: `${(step / 4) * 100}%` }}
            ></div>
          </div>

          <div className="mb-6 flex justify-between items-center font-mono text-xs text-gray-400 font-bold">
            <span>STAGE {Math.min(step, 4)} OF 4</span>
            <span className="text-[#2E3A9E] uppercase">
              {step === 1 && "Business Structure"}
              {step === 2 && "Primary Bottleneck"}
              {step === 3 && "Desired Acceleration"}
              {step === 4 && "Final Proposal Delivery"}
              {step === 5 && "Success Complete"}
            </span>
          </div>

          {/* STEP 1: Business Stage */}
          {step === 1 && (
            <div data-testid={HOME.assessmentStep} className="space-y-6">
              <h3 className="font-heading text-2xl font-extrabold text-[#0A0A0A]">
                What is your company{"'"}s current business profile?
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {[
                  { value: "Brand Owner", desc: "Private label with own manufactured or trademarked items." },
                  { value: "Manufacturer", desc: "Large scale factory production supplying wholesale and stock." },
                  { value: "Retailer / Reseller", desc: "Sourcing catalog items from other brands to retail online." },
                  { value: "New Entrepreneur", desc: "Pre-launch or starting a fresh e-commerce catalog journey." }
                ].map((opt) => (
                  <button
                    key={opt.value}
                    data-testid={`${HOME.assessmentOption}-${opt.value.toLowerCase().replace(/\s+/g, '-')}`}
                    onClick={() => handleOptionSelect("businessStage", opt.value)}
                    className={`p-6 border text-left rounded-md transition-all ${
                      answers.businessStage === opt.value
                        ? "border-[#2E3A9E] bg-blue-50/25 ring-1 ring-[#2E3A9E]"
                        : "border-[#E5E5E5] bg-white hover:bg-gray-50/50"
                    }`}
                  >
                    <span className="font-heading text-base font-extrabold block text-[#0A0A0A] mb-1">
                      {opt.value}
                    </span>
                    <span className="font-body text-xs text-gray-500 leading-relaxed block">
                      {opt.desc}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* STEP 2: Friction Bottleneck */}
          {step === 2 && (
            <div data-testid={HOME.assessmentStep} className="space-y-6">
              <h3 className="font-heading text-2xl font-extrabold text-[#0A0A0A]">
                What is your primary commercial friction point?
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {[
                  { value: "No online presence on any marketplace", desc: "Need immediate end-to-end registration & launch." },
                  { value: "Struggling with Cataloging and Image approvals", desc: "Platform compliance rejections are delaying listings." },
                  { value: "Low organic sales and skyrocketing Ad cost (ACoS)", desc: "Need listing SEO copy updates and structured ad tuning." },
                  { value: "Operational sync and inventory supply constraints", desc: "Multi-channel operations are causing stock outages." }
                ].map((opt) => (
                  <button
                    key={opt.value}
                    data-testid={`${HOME.assessmentOption}-${opt.value.substring(0, 10).toLowerCase().replace(/\s+/g, '-')}`}
                    onClick={() => handleOptionSelect("mainFriction", opt.value)}
                    className={`p-6 border text-left rounded-md transition-all ${
                      answers.mainFriction === opt.value
                        ? "border-[#2E3A9E] bg-blue-50/25 ring-1 ring-[#2E3A9E]"
                        : "border-[#E5E5E5] bg-white hover:bg-gray-50/50"
                    }`}
                  >
                    <span className="font-heading text-base font-extrabold block text-[#0A0A0A] mb-1">
                      {opt.value}
                    </span>
                    <span className="font-body text-xs text-gray-500 leading-relaxed block">
                      {opt.desc}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* STEP 3: Growth speed Choice */}
          {step === 3 && (
            <div data-testid={HOME.assessmentStep} className="space-y-6">
              <h3 className="font-heading text-2xl font-extrabold text-[#0A0A0A]">
                What is your target launch velocity and timeline?
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {[
                  { value: "Aggressive scaling", desc: "Rapid omni-channel deployment across 4+ platforms simultaneously." },
                  { value: "Steady growth", desc: "Focused sequential launch on Amazon and Flipkart first." },
                  { value: "Lean pilot launch", desc: "Single platform testing (e.g. Meesho or Myntra) with minimum budget." }
                ].map((opt) => (
                  <button
                    key={opt.value}
                    data-testid={`${HOME.assessmentOption}-${opt.value.substring(0, 10).toLowerCase().replace(/\s+/g, '-')}`}
                    onClick={() => handleOptionSelect("speedChoice", opt.value)}
                    className={`p-6 border text-left rounded-md transition-all flex flex-col justify-between ${
                      answers.speedChoice === opt.value
                        ? "border-[#2E3A9E] bg-blue-50/25 ring-1 ring-[#2E3A9E]"
                        : "border-[#E5E5E5] bg-white hover:bg-gray-50/50"
                    }`}
                  >
                    <span className="font-heading text-base font-extrabold block text-[#0A0A0A] mb-2">
                      {opt.value}
                    </span>
                    <span className="font-body text-xs text-gray-500 leading-relaxed block">
                      {opt.desc}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* STEP 4: Lead Form + Recommendation */}
          {step === 4 && (
            <div data-testid={HOME.assessmentStep} className="space-y-8">
              <div className="bg-blue-50/50 border border-[#2E3A9E]/30 p-6 rounded-md">
                <h4 className="font-heading text-lg font-extrabold text-[#2E3A9E] mb-2 flex items-center gap-2">
                  <Sparkles size={18} /> Pre-Audit Plan Formulated!
                </h4>
                <p className="font-body text-sm text-[#0A0A0A] leading-relaxed mb-4">
                  Based on your answers, we have analyzed that your <strong>{answers.businessStage}</strong> can resolve <strong>{answers.mainFriction}</strong> through our consulting structure. 
                </p>
                <div className="flex justify-between font-mono text-xs text-[#2E3A9E] font-bold border-t border-blue-200/50 pt-2">
                  <span>Target Growth Velocity:</span>
                  <span className="text-[#6B1414] uppercase">{answers.speedChoice}</span>
                </div>
              </div>

              {/* Minimal Lead Capture Form */}
              <form onSubmit={handleFormSubmit} noValidate className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block font-mono text-[10px] uppercase tracking-wider text-gray-500 mb-1 font-bold">
                      Your Name *
                    </label>
                    <input
                      type="text"
                      name="clientName"
                      data-testid={HOME.leadNameInput}
                      value={leadForm.clientName}
                      onChange={handleInputChange}
                      placeholder="e.g. Rahul Sharma"
                      required
                      className="w-full bg-white border border-[#E5E5E5] p-3 font-body text-sm text-[#0A0A0A] focus:outline-none focus:ring-2 focus:ring-[#2E3A9E] rounded-md"
                    />
                  </div>

                  <div>
                    <label className="block font-mono text-[10px] uppercase tracking-wider text-gray-500 mb-1 font-bold">
                      Business Name *
                    </label>
                    <input
                      type="text"
                      name="businessName"
                      data-testid={HOME.leadBusinessInput}
                      value={leadForm.businessName}
                      onChange={handleInputChange}
                      placeholder="e.g. Calium Garments"
                      required
                      className="w-full bg-white border border-[#E5E5E5] p-3 font-body text-sm text-[#0A0A0A] focus:outline-none focus:ring-2 focus:ring-[#2E3A9E] rounded-md"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block font-mono text-[10px] uppercase tracking-wider text-gray-500 mb-1 font-bold">
                      Corporate Email *
                    </label>
                    <input
                      type="email"
                      name="email"
                      data-testid={HOME.leadEmailInput}
                      value={leadForm.email}
                      onChange={handleInputChange}
                      placeholder="e.g. rahul@yourbrand.com"
                      required
                      className="w-full bg-white border border-[#E5E5E5] p-3 font-body text-sm text-[#0A0A0A] focus:outline-none focus:ring-2 focus:ring-[#2E3A9E] rounded-md"
                    />
                  </div>

                  <div>
                    <label className="block font-mono text-[10px] uppercase tracking-wider text-gray-500 mb-1 font-bold">
                      Phone Number *
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      data-testid={HOME.leadPhoneInput}
                      value={leadForm.phone}
                      onChange={handleInputChange}
                      placeholder="e.g. +91 98765 43210"
                      required
                      className="w-full bg-white border border-[#E5E5E5] p-3 font-body text-sm text-[#0A0A0A] focus:outline-none focus:ring-2 focus:ring-[#2E3A9E] rounded-md"
                    />
                  </div>
                </div>

                <div>
                  <label className="block font-mono text-[10px] uppercase tracking-wider text-gray-500 mb-1 font-bold">
                    Additional Message / Marketplace SKU Details (Optional)
                  </label>
                  <textarea
                    name="message"
                    data-testid={HOME.leadMessageInput}
                    value={leadForm.message}
                    onChange={handleInputChange}
                    placeholder="Describe any existing listings or products you hold..."
                    rows="3"
                    className="w-full bg-white border border-[#E5E5E5] p-3 font-body text-sm text-[#0A0A0A] focus:outline-none focus:ring-2 focus:ring-[#2E3A9E] rounded-md"
                  ></textarea>
                </div>

                {formError && (
                  <p data-testid="lead-form-error" className="font-body text-sm text-[#6B1414] bg-red-50 border border-red-200 rounded-md p-3">{formError}</p>
                )}

                <button
                  type="submit"
                  data-testid={HOME.leadSubmitBtn}
                  disabled={isSubmitting}
                  className="w-full font-body font-bold text-sm uppercase tracking-wide py-4 bg-[#6B1414] text-white hover:bg-[#521010] transition-colors flex items-center justify-center gap-2 rounded-md"
                >
                  {isSubmitting ? "Generating Custom Proposal..." : "Request Growth Audit Roadmap"} <ArrowRight size={16} />
                </button>
              </form>
            </div>
          )}

          {/* STEP 5: Success screen */}
          {step === 5 && (
            <div data-testid={HOME.leadSuccessAlert} className="text-center py-10 space-y-6">
              <div className="w-16 h-16 bg-blue-50 border border-blue-200 rounded-full flex items-center justify-center mx-auto text-[#2E3A9E]">
                <Check size={32} strokeWidth={3} />
              </div>
              <div className="space-y-2">
                <h3 className="font-heading text-3xl font-extrabold text-[#0A0A0A]">
                  Roadmap Proposal Requested!
                </h3>
                <p className="font-body text-base text-[#6B7280] max-w-lg mx-auto">
                  Excellent! Our senior marketplace consultants will analyze your catalog and reach out within 24 business hours. Prefer to talk now? Call us at +91 78273 04742 or write to info@calium.in.
                </p>
              </div>
              <div className="pt-4">
                <button
                  onClick={() => setStep(1)}
                  className="font-body font-bold text-xs uppercase tracking-wider px-6 py-3 bg-[#2E3A9E] text-white hover:bg-[#232D7C] rounded-md"
                >
                  Run Another Audit
                </button>
              </div>
            </div>
          )}

          {/* Controls Footer */}
          {step < 4 && (
            <div className="mt-10 pt-6 border-t border-[#E5E5E5] flex justify-between">
              <button
                data-testid={HOME.assessmentPrevBtn}
                onClick={handlePrev}
                disabled={step === 1}
                className="font-body font-bold text-xs uppercase tracking-wide text-gray-400 hover:text-[#0A0A0A] disabled:opacity-30 disabled:pointer-events-none flex items-center gap-1"
              >
                <ArrowLeft size={14} /> Back
              </button>

              <button
                data-testid={HOME.assessmentNextBtn}
                onClick={handleNext}
                className="font-body font-bold text-xs uppercase tracking-wide px-6 py-3 bg-[#2E3A9E] text-white hover:bg-[#232D7C] flex items-center gap-1 rounded-md"
              >
                Next Step <ArrowRight size={14} />
              </button>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

// Case Studies/Success Stories
const CaseStudiesSection = ({ studies, loading }) => {
  return (
    <section id="case-studies" className="py-24 bg-[#FAFAFA] border-b border-[#E5E5E5]">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="mb-16 space-y-4">
          <span className="font-mono text-xs tracking-wider uppercase text-[#6B1414] font-bold">
            08 / Proof of Concept
          </span>
          <h2 className="font-heading text-4xl sm:text-5xl font-extrabold tracking-tighter text-[#0A0A0A]">
            Real Results, Compounded Metrics
          </h2>
          <p className="font-body text-base text-[#6B7280] max-w-3xl leading-relaxed">
            We don{"'"}t talk hypotheses. We execute. Explore how we transformed small regional catalogs into top-selling national merchant operations on Flipkart, Amazon, and Myntra.
          </p>
        </div>

        {loading ? (
          <div className="text-center py-20 font-mono text-xs text-gray-500">
            LOADING COMPOUND CASE STUDIES...
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {studies.map((s, i) => (
              <div 
                key={s.id || i}
                data-testid={HOME.caseStudyCard}
                className="bg-white border border-[#E5E5E5] p-8 flex flex-col justify-between rounded-md relative hover:border-gray-400 transition-colors"
              >
                {/* Accent Top Bar */}
                <div className="absolute top-0 left-0 w-full h-1 bg-[#6B1414]"></div>
                
                <div className="space-y-6">
                  <div className="flex justify-between items-start">
                    <div>
                      <span className="font-mono text-[10px] text-gray-400 uppercase tracking-widest block">
                        Client Brand
                      </span>
                      <h3 className="font-heading text-xl font-bold text-[#0A0A0A]">
                        {s.client_name}
                      </h3>
                    </div>
                    <span className="font-mono text-[10px] text-[#2E3A9E] bg-blue-50 px-2 py-0.5 border border-blue-100 uppercase rounded font-bold">
                      {s.marketplace}
                    </span>
                  </div>

                  {/* Growth Block */}
                  <div className="bg-gray-50 border border-gray-100 p-4 flex justify-between items-center">
                    <div>
                      <span className="font-mono text-[9px] text-gray-400 uppercase block">
                        Monthly revenue Growth
                      </span>
                      <span className="font-heading text-2xl font-black text-[#2E3A9E]">
                        +{s.growth_percentage}%
                      </span>
                    </div>
                    <div className="text-right">
                      <span className="font-mono text-[9px] text-gray-400 uppercase block">
                        Post-Launch Scale
                      </span>
                      <span className="font-body text-sm font-bold text-[#0A0A0A]">
                        ₹{s.revenue_after?.toLocaleString('en-IN') || "0"}
                      </span>
                    </div>
                  </div>

                  <div className="space-y-3 font-body text-xs">
                    <div>
                      <span className="text-[#6B1414] font-bold block uppercase font-mono text-[9px] tracking-wide mb-1">
                        Challenge
                      </span>
                      <p className="text-gray-600 leading-relaxed">
                        {s.challenge}
                      </p>
                    </div>
                    <div>
                      <span className="text-[#2E3A9E] font-bold block uppercase font-mono text-[9px] tracking-wide mb-1">
                        Consulting Intervention
                      </span>
                      <p className="text-[#0A0A0A] leading-relaxed">
                        {s.solution}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="mt-8 pt-4 border-t border-[#E5E5E5] flex justify-between items-center font-mono text-[10px] text-gray-400">
                  <span>BASELINE: ₹{s.revenue_before?.toLocaleString('en-IN') || "0"}</span>
                  <span>100% VERIFIED SUCCESS</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

// Main Landing Page wrapper
const LandingPage = ({ studies, loading, onLeadSuccess }) => {
  return (
    <div className="bg-[#FAFAFA] font-body min-h-screen">
      <Hero />
      <Partners />
      <Reveal><ServicesSection /></Reveal>
      <Process />
      <WhyCalium />
      <Reveal><MarketplacesGrid /></Reveal>
      <Testimonials />
      <Reveal><GrowthEstimator /></Reveal>
      <Reveal><OnboardingWizard onLeadSuccess={onLeadSuccess} /></Reveal>
      <Reveal><CaseStudiesSection studies={studies} loading={loading} /></Reveal>
      <FAQ />
      <ContactForm onSuccess={onLeadSuccess} />
      <BlogPreview />
    </div>
  );
};

const ProtectedRoute = ({ children }) => {
  const { user } = useAuth();
  if (user === null) return <div className="pt-40 text-center font-mono text-xs text-gray-400" data-testid="auth-checking">CHECKING SESSION...</div>;
  if (!user) return <Navigate to="/admin/login" replace />;
  return children;
};

// Admin Control Panel
const AdminDashboard = ({ studies, leads, loadingLeads, loadingStudies, onRefresh, onLogout, notify, currentUser }) => {
  const [activeTab, setActiveTab] = useState("leads"); // "leads" or "case-studies"
  
  // Lead Update Status State
  const [updatingLeadId, setUpdatingLeadId] = useState(null);

  // New Case Study Form State
  const [csForm, setCsForm] = useState({
    client_name: "",
    marketplace: "Amazon",
    growth_percentage: "",
    revenue_before: "",
    revenue_after: "",
    challenge: "",
    solution: ""
  });

  const handleStatusChange = async (leadId, newStatus) => {
    try {
      await api.patch(`/leads/${leadId}/status`, { status: newStatus });
      onRefresh();
    } catch (e) {
      console.error("Failed to update status", e);
      alert("Error updating lead status.");
    }
  };

  const handleDeleteLead = async (leadId) => {
    if (!window.confirm("Are you sure you want to delete this lead?")) return;
    try {
      await api.delete(`/leads/${leadId}`);
      onRefresh();
    } catch (e) {
      console.error("Failed to delete lead", e);
      alert("Error deleting lead.");
    }
  };

  const handleCsInputChange = (e) => {
    setCsForm({ ...csForm, [e.target.name]: e.target.value });
  };

  const handleCsSubmit = async (e) => {
    e.preventDefault();
    if (!csForm.client_name || !csForm.growth_percentage || !csForm.revenue_before || !csForm.revenue_after || !csForm.challenge || !csForm.solution) {
      alert("Please fill in all case study fields.");
      return;
    }

    try {
      const payload = {
        ...csForm,
        growth_percentage: Number(csForm.growth_percentage),
        revenue_before: Number(csForm.revenue_before),
        revenue_after: Number(csForm.revenue_after)
      };
      await api.post(`/case-studies`, payload);
      setCsForm({
        client_name: "",
        marketplace: "Amazon",
        growth_percentage: "",
        revenue_before: "",
        revenue_after: "",
        challenge: "",
        solution: ""
      });
      onRefresh();
    } catch (err) {
      console.error("Failed to create case study", err);
      alert("Error creating case study.");
    }
  };

  const handleDeleteCs = async (csId) => {
    if (!window.confirm("Are you sure you want to delete this case study?")) return;
    try {
      await api.delete(`/case-studies/${csId}`);
      onRefresh();
    } catch (err) {
      console.error("Failed to delete case study", err);
      alert("Error deleting case study.");
    }
  };

  // Metrics helper
  const totalLeads = leads.length;
  const pendingLeads = leads.filter(l => l.status === "Pending").length;
  const contactedLeads = leads.filter(l => l.status === "Contacted").length;
  const avgProjection = totalLeads 
    ? leads.reduce((acc, curr) => acc + (curr.growth_projection || 0), 0) / totalLeads 
    : 0;

  return (
    <div className="pt-20 min-h-screen bg-white font-body flex flex-col md:flex-row select-none">
      {/* Sidebar - Dark theme Swiss brutal style */}
      <aside className="w-full md:w-64 bg-[#0A0A0A] text-white p-4 md:p-6 flex flex-col justify-between border-r border-[#E5E5E5]">
        <div className="space-y-4 md:space-y-8">
          <div className="flex items-center gap-3 md:block">
            <div className="bg-white rounded-md p-1.5 md:p-2 w-fit md:mb-4">
              <img src="/logo-mark.png" alt="Calium logo" className="h-8 w-8 md:h-10 md:w-10 object-contain" />
            </div>
            <div>
              <span className="font-mono text-[9px] text-[#C8956A] tracking-widest uppercase font-bold block md:mb-1">
                Command Center
              </span>
              <h2 className="font-heading text-lg md:text-xl font-bold tracking-tight text-white">
                Control Room
              </h2>
            </div>
          </div>

          <div data-testid="admin-tab-bar" className="flex md:flex-col gap-1 overflow-x-auto md:overflow-visible -mx-4 px-4 md:mx-0 md:px-0 pb-1 md:pb-0 [&>button]:whitespace-nowrap [&>button]:w-auto md:[&>button]:w-full [&>button]:shrink-0">
            <button
              data-testid="admin-tab-leads"
              onClick={() => setActiveTab("leads")}
              className={`w-full text-left p-3 font-mono text-xs uppercase tracking-wider rounded-md flex items-center gap-3 transition-colors ${
                activeTab === "leads" 
                  ? "bg-[#2E3A9E] text-white font-bold" 
                  : "text-gray-400 hover:bg-gray-900 hover:text-white"
              }`}
            >
              <Users size={16} /> Leads Inbox ({totalLeads})
            </button>

            <button
              data-testid="admin-tab-case-studies"
              onClick={() => setActiveTab("case-studies")}
              className={`w-full text-left p-3 font-mono text-xs uppercase tracking-wider rounded-md flex items-center gap-3 transition-colors ${
                activeTab === "case-studies" 
                  ? "bg-[#2E3A9E] text-white font-bold" 
                  : "text-gray-400 hover:bg-gray-900 hover:text-white"
              }`}
            >
              <BarChart size={16} /> Case Studies CRUD
            </button>

            <button
              data-testid="admin-tab-testimonials"
              onClick={() => setActiveTab("testimonials")}
              className={`w-full text-left p-3 font-mono text-xs uppercase tracking-wider rounded-md flex items-center gap-3 transition-colors ${
                activeTab === "testimonials" 
                  ? "bg-[#2E3A9E] text-white font-bold" 
                  : "text-gray-400 hover:bg-gray-900 hover:text-white"
              }`}
            >
              <MessageSquareQuote size={16} /> Testimonials
            </button>

            <button
              data-testid="admin-tab-blog"
              onClick={() => setActiveTab("blog")}
              className={`w-full text-left p-3 font-mono text-xs uppercase tracking-wider rounded-md flex items-center gap-3 transition-colors ${
                activeTab === "blog" 
                  ? "bg-[#2E3A9E] text-white font-bold" 
                  : "text-gray-400 hover:bg-gray-900 hover:text-white"
              }`}
            >
              <Newspaper size={16} /> Blog / Insights
            </button>

            <button
              data-testid="admin-tab-team"
              onClick={() => setActiveTab("team")}
              className={`w-full text-left p-3 font-mono text-xs uppercase tracking-wider rounded-md flex items-center gap-3 transition-colors ${
                activeTab === "team" 
                  ? "bg-[#2E3A9E] text-white font-bold" 
                  : "text-gray-400 hover:bg-gray-900 hover:text-white"
              }`}
            >
              <UsersRound size={16} /> Team & Security
            </button>

            <button
              data-testid="admin-logout-btn"
              onClick={onLogout}
              className="w-full text-left p-3 font-mono text-xs uppercase tracking-wider rounded-md flex items-center gap-3 text-[#C8956A] hover:bg-gray-900 hover:text-white transition-colors md:mt-6"
            >
              <LogOut size={16} /> Sign out
            </button>
          </div>
        </div>

        <div className="hidden md:block mt-12 pt-6 border-t border-gray-800 text-gray-500 font-mono text-[10px] space-y-1">
          {currentUser && (
            <div data-testid="admin-current-user" className="mb-3 flex items-center gap-2 text-gray-300">
              <span className="w-7 h-7 rounded-full bg-[#6B1414] text-[#F5D5B0] font-heading font-black flex items-center justify-center text-xs">{(currentUser.name || currentUser.email)[0]?.toUpperCase()}</span>
              <span className="min-w-0"><span className="block text-white font-bold truncate">{currentUser.name}</span><span className="block truncate">{currentUser.email}</span></span>
            </div>
          )}
          <p>Calium Seller Services</p>
          <p>© 2026 Active System</p>
        </div>
      </aside>

      {/* Main Control Panel area */}
      <main className="flex-1 p-4 sm:p-6 md:p-10 space-y-8 overflow-x-hidden bg-[#FAFAFA] min-w-0">
        {/* Header summary of current status */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 border-b border-[#E5E5E5] pb-6">
          <div>
            <h3 className="font-heading text-2xl md:text-3xl font-extrabold tracking-tight text-[#0A0A0A]">
              {{ leads: "Onboarding Leads Submissions", "case-studies": "Manage Case Studies Success Stories", testimonials: "Client Testimonials", blog: "Blog / Insights Articles", team: "Team Accounts & Security" }[activeTab]}
            </h3>
            <p className="font-body text-xs text-gray-500 mt-1">
              Active operational data from MongoDB pipeline.
            </p>
          </div>
          <button 
            onClick={onRefresh}
            className="font-mono text-xs tracking-wide bg-[#2E3A9E] text-white px-4 py-2 border border-blue-400 hover:bg-[#232D7C] rounded transition-all font-bold"
          >
            Refresh Pipeline
          </button>
        </div>

        {/* Dynamic content */}
        {activeTab === "testimonials" && <AdminTestimonials notify={(m) => notify && notify(m)} />}
        {activeTab === "blog" && <AdminBlog notify={(m) => notify && notify(m)} />}
        {activeTab === "team" && <AdminTeam notify={(m, t) => notify && notify(m, t)} />}
        {activeTab === "leads" ? (
          <div className="space-y-8">
            <EmailStatusBanner />
            {/* North Star Metrics Row */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {/* North Star Block */}
              <div className="bg-white border border-[#E5E5E5] p-6 rounded-md shadow-sm relative overflow-hidden">
                <div className="absolute top-0 left-0 w-1 h-full bg-[#2E3A9E]"></div>
                <span className="font-mono text-[10px] uppercase tracking-wider text-gray-400 block mb-1">
                  Total Leads Captured
                </span>
                <span 
                  data-testid={HOME.adminMetricLeads}
                  className="font-heading text-4xl font-black text-[#0A0A0A]"
                >
                  {totalLeads}
                </span>
              </div>

              <div className="bg-white border border-[#E5E5E5] p-6 rounded-md shadow-sm relative overflow-hidden">
                <div className="absolute top-0 left-0 w-1 h-full bg-yellow-500"></div>
                <span className="font-mono text-[10px] uppercase tracking-wider text-gray-400 block mb-1">
                  Leads Pending Status
                </span>
                <span 
                  data-testid={HOME.adminMetricPending}
                  className="font-heading text-4xl font-black text-yellow-600"
                >
                  {pendingLeads}
                </span>
              </div>

              <div className="bg-white border border-[#E5E5E5] p-6 rounded-md shadow-sm relative overflow-hidden">
                <div className="absolute top-0 left-0 w-1 h-full bg-emerald-500"></div>
                <span className="font-mono text-[10px] uppercase tracking-wider text-gray-400 block mb-1">
                  Leads Contacted / Open
                </span>
                <span 
                  data-testid={HOME.adminMetricContacted}
                  className="font-heading text-4xl font-black text-emerald-600"
                >
                  {contactedLeads}
                </span>
              </div>

              <div className="bg-white border border-[#E5E5E5] p-6 rounded-md shadow-sm relative overflow-hidden">
                <div className="absolute top-0 left-0 w-1 h-full bg-[#6B1414]"></div>
                <span className="font-mono text-[10px] uppercase tracking-wider text-gray-400 block mb-1">
                  Avg Growth Projections
                </span>
                <span className="font-heading text-2xl font-black text-[#6B1414] block mt-1 tracking-tight">
                  ₹{Math.round(avgProjection).toLocaleString('en-IN')}
                </span>
              </div>
            </div>

            {/* Leads Table */}
            <div className="bg-white border border-[#E5E5E5] rounded-md shadow-sm overflow-hidden select-none">
              <div className="bg-gray-50 border-b border-[#E5E5E5] p-4 flex justify-between items-center">
                <span className="font-mono text-xs uppercase tracking-wider font-bold text-[#0A0A0A]">
                  Active Leads Registry
                </span>
                <span className="font-mono text-[10px] text-gray-400">
                  {leads.length} Records found
                </span>
              </div>

              {loadingLeads ? (
                <div className="text-center py-20 font-mono text-xs text-gray-500">
                  SYNCING DATA FROM MONGODB CLUSTER...
                </div>
              ) : leads.length === 0 ? (
                <div className="text-center py-20 font-mono text-xs text-gray-500">
                  NO ACTIVE LEADS RECEIVED YET. RUN ONBOARDING WIZARD TO GENERATE.
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-left border-collapse font-body text-xs">
                    <thead>
                      <tr className="bg-gray-100/50 border-b border-[#E5E5E5] font-mono text-[10px] uppercase tracking-wider text-gray-500">
                        <th className="p-4 font-bold">Business / contact</th>
                        <th className="p-4 font-bold">Platform Targets</th>
                        <th className="p-4 font-bold">Roadmap metrics</th>
                        <th className="p-4 font-bold">Lead Status</th>
                        <th className="p-4 font-bold text-right">Operations</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[#E5E5E5]">
                      {leads.map((l) => (
                        <tr 
                          key={l.id} 
                          data-testid={HOME.adminLeadRow}
                          className="hover:bg-gray-50/50 transition-colors"
                        >
                          <td className="p-4 space-y-1">
                            <p className="font-bold text-[#0A0A0A] text-sm">{l.business_name}</p>
                            <p className="text-gray-500 font-semibold">{l.client_name}</p>
                            <div className="flex gap-2 text-[10px] text-gray-400 font-mono pt-1">
                              <span className="flex items-center gap-0.5"><Mail size={10} /> {l.email}</span>
                              <span className="flex items-center gap-0.5"><Phone size={10} /> {l.phone}</span>
                            </div>
                          </td>
                          <td className="p-4 space-y-1 font-mono text-[10px]">
                            <span className="bg-blue-50 text-[#2E3A9E] px-2 py-0.5 border border-blue-100 font-bold uppercase rounded block w-fit">
                              {l.primary_marketplace}
                            </span>
                            <span data-testid="admin-lead-source" className={`px-2 py-0.5 border font-bold uppercase rounded block w-fit ${l.source === "contact" ? "bg-red-50 text-[#6B1414] border-red-200" : "bg-gray-50 text-gray-600 border-gray-200"}`}>
                              {l.source === "contact" ? "Contact Form" : "Wizard"}{l.interested_in ? ` · ${l.interested_in}` : ""}
                            </span>
                            <div className="text-gray-500 pt-1 space-y-0.5 max-w-[240px]">
                              {l.assessment && Object.entries(l.assessment).map(([k, v]) => (
                                <p key={k} className="truncate">
                                  <strong>{k}:</strong> {v}
                                </p>
                              ))}
                            </div>
                          </td>
                          <td className="p-4 space-y-1 font-mono text-[10px]">
                            <p className="text-gray-500">PROJECTION:</p>
                            <p className="font-extrabold text-[#6B1414] text-sm">
                              ₹{l.growth_projection?.toLocaleString('en-IN') || "0"}
                            </p>
                            {l.message && <p className="text-gray-400 italic font-body max-w-[200px] truncate">&ldquo;{l.message}&rdquo;</p>}
                          </td>
                          <td className="p-4">
                            <select
                              data-testid={HOME.adminLeadStatusSelect}
                              value={l.status || "Pending"}
                              onChange={(e) => handleStatusChange(l.id, e.target.value)}
                              className={`p-2 border rounded-md font-bold ${
                                l.status === "Pending" ? "bg-yellow-50 text-yellow-700 border-yellow-200" :
                                l.status === "Contacted" ? "bg-emerald-50 text-emerald-700 border-emerald-200" :
                                l.status === "In-Progress" ? "bg-blue-50 text-[#2E3A9E] border-blue-200" :
                                "bg-gray-50 text-gray-700 border-gray-200"
                              }`}
                            >
                              <option value="Pending">Pending</option>
                              <option value="Contacted">Contacted</option>
                              <option value="In-Progress">In-Progress</option>
                              <option value="Closed">Closed</option>
                            </select>
                          </td>
                          <td className="p-4 text-right">
                            <button
                              data-testid={HOME.adminLeadDeleteBtn}
                              onClick={() => handleDeleteLead(l.id)}
                              className="p-2 border border-red-200 text-red-600 hover:bg-red-50 hover:border-red-300 rounded transition-colors"
                              title="Delete Lead Record"
                            >
                              <Trash2 size={14} />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        ) : activeTab === "case-studies" ? (
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
            {/* Create Case Study Form Left-side */}
            <div className="lg:col-span-4 bg-white border border-[#E5E5E5] p-6 rounded-md h-fit shadow-sm relative">
              <div className="absolute top-0 left-0 w-full h-1 bg-[#6B1414]"></div>
              <h4 className="font-heading text-lg font-bold text-[#0A0A0A] mb-4">
                Add Client Case Study
              </h4>

              <form onSubmit={handleCsSubmit} className="space-y-4 font-body text-xs">
                <div>
                  <label className="block font-mono text-[9px] uppercase tracking-wider text-gray-500 mb-1 font-bold">
                    Client Name
                  </label>
                  <input
                    type="text"
                    name="client_name"
                    data-testid={HOME.adminCsClientInput}
                    value={csForm.client_name}
                    onChange={handleCsInputChange}
                    placeholder="e.g. EcoGlow Oils"
                    required
                    className="w-full bg-white border border-[#E5E5E5] p-3 text-[#0A0A0A] focus:outline-none focus:ring-2 focus:ring-[#2E3A9E] rounded-md"
                  />
                </div>

                <div>
                  <label className="block font-mono text-[9px] uppercase tracking-wider text-gray-500 mb-1 font-bold">
                    Marketplace Platform
                  </label>
                  <select
                    name="marketplace"
                    data-testid={HOME.adminCsMarketplaceSelect}
                    value={csForm.marketplace}
                    onChange={handleCsInputChange}
                    className="w-full bg-white border border-[#E5E5E5] p-3 text-[#0A0A0A] focus:outline-none focus:ring-2 focus:ring-[#2E3A9E] rounded-md font-bold"
                  >
                    <option value="Amazon">Amazon India</option>
                    <option value="Flipkart">Flipkart</option>
                    <option value="Meesho">Meesho</option>
                    <option value="Myntra">Myntra</option>
                    <option value="Ajio">Ajio</option>
                    <option value="JioMart">JioMart</option>
                  </select>
                </div>

                <div>
                  <label className="block font-mono text-[9px] uppercase tracking-wider text-gray-500 mb-1 font-bold">
                    Growth Percentage (%)
                  </label>
                  <input
                    type="number"
                    name="growth_percentage"
                    data-testid={HOME.adminCsGrowthInput}
                    value={csForm.growth_percentage}
                    onChange={handleCsInputChange}
                    placeholder="e.g. 240"
                    required
                    className="w-full bg-white border border-[#E5E5E5] p-3 text-[#0A0A0A] focus:outline-none focus:ring-2 focus:ring-[#2E3A9E] rounded-md font-bold"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block font-mono text-[9px] uppercase tracking-wider text-gray-500 mb-1 font-bold">
                      Revenue Before (₹)
                    </label>
                    <input
                      type="number"
                      name="revenue_before"
                      data-testid={HOME.adminCsRevenueBeforeInput}
                      value={csForm.revenue_before}
                      onChange={handleCsInputChange}
                      placeholder="e.g. 300000"
                      required
                      className="w-full bg-white border border-[#E5E5E5] p-3 text-[#0A0A0A] focus:outline-none focus:ring-2 focus:ring-[#2E3A9E] rounded-md"
                    />
                  </div>

                  <div>
                    <label className="block font-mono text-[9px] uppercase tracking-wider text-gray-500 mb-1 font-bold">
                      Revenue After (₹)
                    </label>
                    <input
                      type="number"
                      name="revenue_after"
                      data-testid={HOME.adminCsRevenueAfterInput}
                      value={csForm.revenue_after}
                      onChange={handleCsInputChange}
                      placeholder="e.g. 1020000"
                      required
                      className="w-full bg-white border border-[#E5E5E5] p-3 text-[#0A0A0A] focus:outline-none focus:ring-2 focus:ring-[#2E3A9E] rounded-md"
                    />
                  </div>
                </div>

                <div>
                  <label className="block font-mono text-[9px] uppercase tracking-wider text-gray-500 mb-1 font-bold">
                    Main Challenge
                  </label>
                  <textarea
                    name="challenge"
                    data-testid={HOME.adminCsChallengeInput}
                    value={csForm.challenge}
                    onChange={handleCsInputChange}
                    placeholder="Describe product constraints..."
                    rows="2"
                    required
                    className="w-full bg-white border border-[#E5E5E5] p-3 text-[#0A0A0A] focus:outline-none focus:ring-2 focus:ring-[#2E3A9E] rounded-md"
                  ></textarea>
                </div>

                <div>
                  <label className="block font-mono text-[9px] uppercase tracking-wider text-gray-500 mb-1 font-bold">
                    Solution Implemented
                  </label>
                  <textarea
                    name="solution"
                    data-testid={HOME.adminCsSolutionInput}
                    value={csForm.solution}
                    onChange={handleCsInputChange}
                    placeholder="How did Calium help launch/scale?"
                    rows="2"
                    required
                    className="w-full bg-white border border-[#E5E5E5] p-3 text-[#0A0A0A] focus:outline-none focus:ring-2 focus:ring-[#2E3A9E] rounded-md"
                  ></textarea>
                </div>

                <button
                  type="submit"
                  data-testid={HOME.adminCsSubmitBtn}
                  className="w-full font-mono text-xs uppercase tracking-wider font-bold py-3 bg-[#2E3A9E] text-white hover:bg-[#232D7C] rounded-md transition-colors"
                >
                  Create Success Story
                </button>
              </form>
            </div>

            {/* Case Studies List Table Right-side */}
            <div className="lg:col-span-8 bg-white border border-[#E5E5E5] rounded-md shadow-sm overflow-hidden h-fit">
              <div className="bg-gray-50 border-b border-[#E5E5E5] p-4 flex justify-between items-center">
                <span className="font-mono text-xs uppercase tracking-wider font-bold text-[#0A0A0A]">
                  Active Case Studies Registry
                </span>
                <span className="font-mono text-[10px] text-gray-400">
                  {studies.length} Stories available
                </span>
              </div>

              {loadingStudies ? (
                <div className="text-center py-20 font-mono text-xs text-gray-500">
                  PULLING COMPOUND ANALYTICS...
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-left border-collapse font-body text-xs">
                    <thead>
                      <tr className="bg-gray-100/50 border-b border-[#E5E5E5] font-mono text-[10px] uppercase tracking-wider text-gray-500">
                        <th className="p-4 font-bold">Client / Marketplace</th>
                        <th className="p-4 font-bold">Growth Scale</th>
                        <th className="p-4 font-bold">Solution Details</th>
                        <th className="p-4 font-bold text-right">Delete</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[#E5E5E5]">
                      {studies.map((s) => (
                        <tr 
                          key={s.id} 
                          data-testid={HOME.adminCsRow}
                          className="hover:bg-gray-50/50 transition-colors"
                        >
                          <td className="p-4 space-y-1">
                            <p className="font-bold text-[#0A0A0A] text-sm">{s.client_name}</p>
                            <span className="font-mono text-[10px] bg-blue-50 text-[#2E3A9E] px-2 py-0.5 border border-blue-100 font-bold uppercase rounded block w-fit">
                              {s.marketplace}
                            </span>
                          </td>
                          <td className="p-4 font-mono space-y-1">
                            <span className="text-[#6B1414] font-black text-sm block">+{s.growth_percentage}%</span>
                            <span className="text-gray-400 text-[10px] block">
                              ₹{s.revenue_before?.toLocaleString('en-IN') || "0"} → ₹{s.revenue_after?.toLocaleString('en-IN') || "0"}
                            </span>
                          </td>
                          <td className="p-4 space-y-1 max-w-[280px]">
                            <p className="text-gray-500 truncate" title={s.challenge}>
                              <strong>Challenge:</strong> {s.challenge}
                            </p>
                            <p className="text-[#0A0A0A] truncate" title={s.solution}>
                              <strong>Solution:</strong> {s.solution}
                            </p>
                          </td>
                          <td className="p-4 text-right">
                            <button
                              data-testid={HOME.adminCsDeleteBtn}
                              onClick={() => handleDeleteCs(s.id)}
                              className="p-2 border border-red-200 text-red-600 hover:bg-red-50 hover:border-red-300 rounded transition-colors"
                              title="Delete success story"
                            >
                              <Trash2 size={14} />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        ) : null}
      </main>
    </div>
  );
};

// Footer Component
const Footer = () => {
  return (
    <footer className="bg-[#0A0A0A] text-white pt-20 pb-10 border-t border-gray-800">
      <div className="max-w-7xl mx-auto px-6 lg:px-8 grid grid-cols-1 md:grid-cols-12 gap-12 border-b border-gray-800 pb-16">
        <div className="md:col-span-5 space-y-6">
          <div className="flex items-center gap-3">
            <div className="bg-white rounded-md p-1.5">
              <img src="/logo-mark.png" alt="Calium Seller Services logo" className="h-12 w-12 object-contain" />
            </div>
            <span className="flex flex-col leading-none">
              <span className="font-heading text-2xl font-extrabold tracking-tighter text-white">Calium</span>
              <span className="font-mono text-[9px] tracking-[0.2em] uppercase text-[#C8956A] font-bold">Seller Services</span>
            </span>
          </div>
          <p className="font-body text-xs text-gray-400 max-w-sm leading-relaxed">
            Calium Seller Services is the brand of Calium Technologies Private Limited, an IT services and e-commerce consulting firm founded in 2017 in New Delhi. Trusted by 5000+ Amazon.in and 2000+ Flipkart sellers across India.
          </p>
          <a href="https://www.calium.in" target="_blank" rel="noreferrer" data-testid="footer-website-link" className="inline-flex items-center gap-1 font-mono text-[10px] text-[#C8956A] hover:text-white transition-colors uppercase tracking-wider">
            <Globe size={12} /> www.calium.in
          </a>
        </div>

        <div className="md:col-span-4 space-y-4">
          <h4 className="font-mono text-[10px] uppercase tracking-widest text-[#C8956A] font-bold">
            Services Catalog
          </h4>
          <ul className="font-body text-xs text-gray-400 space-y-2">
            {SERVICES.map((s) => (
              <li key={s.slug}><Link to={`/services/${s.slug}`} className="hover:text-[#C8956A] transition-colors">• {s.title}</Link></li>
            ))}
            <li className="pt-2"><Link to="/security" data-testid="footer-security-link" className="text-[#C8956A] hover:text-white transition-colors font-bold">• Security &amp; Data Protection</Link></li>
          </ul>
        </div>

        <div className="md:col-span-3 space-y-4">
          <h4 className="font-mono text-[10px] uppercase tracking-widest text-[#C8956A] font-bold">
            Head Office
          </h4>
          <p className="font-body text-xs text-gray-400 leading-relaxed" data-testid="footer-address">
            Calium Technologies Private Limited <br />
            A-42, 3rd Floor, Gurunanakpura, <br />
            Laxmi Nagar, <br />
            Delhi 110092, India
          </p>
          <p className="font-mono text-[10px] text-gray-500 leading-relaxed" data-testid="footer-registration">
            CIN: {COMPANY_PROFILE.cin} <br />
            GSTIN: {COMPANY_PROFILE.gstin}
          </p>
          <p className="font-body text-xs text-gray-400 leading-relaxed space-y-1">
            <a href="tel:+917827304742" className="block text-white font-semibold hover:text-[#C8956A] transition-colors">+91 78273 04742</a>
            <a href="mailto:info@calium.in" className="block text-white font-semibold hover:text-[#C8956A] transition-colors">info@calium.in</a>
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-8 pt-10 flex flex-col sm:flex-row justify-between items-center gap-4 text-gray-500 font-mono text-[10px]">
        <p>© 2026 Calium Technologies Private Limited. All Rights Reserved.</p>
        <p>Amazon · Flipkart · Meesho · Myntra · Ajio · JioMart · Snapdeal</p>
      </div>
    </footer>
  );
};

// Main App Router & State Sync
function AppShell() {
  const { user, logout } = useAuth();
  useLenis();
  const [studies, setStudies] = useState([]);
  const [leads, setLeads] = useState([]);
  const [loadingStudies, setLoadingStudies] = useState(true);
  const [loadingLeads, setLoadingLeads] = useState(true);
  const [toast, setToast] = useState(null);

  const fetchStudies = async () => {
    try {
      setLoadingStudies(true);
      const res = await api.get(`/case-studies`);
      setStudies(res.data);
    } catch (e) {
      console.error("Error fetching studies", e);
    } finally {
      setLoadingStudies(false);
    }
  };

  const fetchLeads = async () => {
    try {
      setLoadingLeads(true);
      const res = await api.get(`/leads`);
      setLeads(res.data);
    } catch (e) {
      console.error("Error fetching leads", e);
    } finally {
      setLoadingLeads(false);
    }
  };

  const triggerToast = (message, type = "success") => {
    setToast({ message, type });
  };

  const handleLeadSuccess = () => {
    triggerToast("Your growth audit request has been received. Our team has been notified.", "success");
    if (user) fetchLeads();
  };

  useEffect(() => {
    fetchStudies();
  }, []);

  useEffect(() => {
    if (user) fetchLeads();
  }, [user]);

  return (
    <BrowserRouter>
      <div className="min-h-screen bg-[#FAFAFA] flex flex-col justify-between font-body text-[#0A0A0A]">
        <Navbar />
        
        {/* Dynamic Route Content */}
        <div className="flex-grow">
          <Routes>
            <Route 
              path="/" 
              element={
                <LandingPage 
                  studies={studies} 
                  loading={loadingStudies} 
                  onLeadSuccess={handleLeadSuccess} 
                />
              } 
            />
            <Route path="/services" element={<ServicesPage studies={studies} />} />
            <Route path="/services/:slug" element={<ServicesPage studies={studies} />} />
            <Route path="/security" element={<SecurityPage />} />
            <Route path="/blog" element={<BlogListPage />} />
            <Route path="/blog/:slug" element={<BlogPostPage />} />
            <Route path="/admin/login" element={user ? <Navigate to="/admin" replace /> : <AdminLogin />} />
            <Route path="/admin/forgot" element={<ForgotPasswordPage />} />
            <Route path="/admin/reset" element={<SetPasswordPage mode="reset" />} />
            <Route path="/admin/verify" element={<SetPasswordPage mode="verify" />} />
            <Route 
              path="/admin" 
              element={
                <ProtectedRoute>
                  <AdminDashboard 
                    studies={studies} 
                    leads={leads}
                    loadingLeads={loadingLeads}
                    loadingStudies={loadingStudies}
                    currentUser={user}
                    notify={(m, t) => triggerToast(m, t || "success")}
                    onLogout={async () => { await logout(); triggerToast("Signed out of Control Room", "success"); }}
                    onRefresh={() => {
                      fetchStudies();
                      fetchLeads();
                      triggerToast("MongoDB Pipeline Synced!", "success");
                    }}
                  />
                </ProtectedRoute>
              } 
            />
          </Routes>
        </div>
        <WhatsAppButton />

        {/* Global Toast Alerts */}
        {toast && (
          <CustomToast 
            message={toast.message} 
            type={toast.type} 
            onClose={() => setToast(null)} 
          />
        )}

        <Footer />
      </div>
    </BrowserRouter>
  );
}

function App() {
  return (
    <AuthProvider>
      <AppShell />
    </AuthProvider>
  );
}

export default App;
